import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { GroupsOutlined, CampaignOutlined } from "@mui/icons-material";
import PageHeader from "../../common/PageHeader";
import { GlobalContext } from "../../context/GlobalContext";

const SEGMENTS = [
  {
    key: "marketing_opt_in",
    label: "Marketing opt-in",
    audience: "marketing_opt_in",
    desc: "Recommended for most blasts. Respects notification prefs.",
  },
  {
    key: "paid",
    label: "Paid plans",
    audience: "paid",
    desc: "Users on a paid plan (opt-in only). Good for upsell / feature launches.",
  },
  {
    key: "free",
    label: "Free / trial",
    audience: "free",
    desc: "Free and trial users. Ideal for activation and upgrade nudges.",
  },
  {
    key: "signed_up_7d",
    label: "Signed up · 7 days",
    audience: "signed_up_7d",
    desc: "Brand-new accounts. Pair with onboarding tips.",
  },
  {
    key: "signed_up_30d",
    label: "Signed up · 30 days",
    audience: "signed_up_30d",
    desc: "Recent cohort — win-back or first-purchase campaigns.",
  },
  {
    key: "all_active",
    label: "All active (opt-in)",
    audience: "all_active",
    desc: "Every active opted-in user with an email.",
  },
];

export default function MarketingAudiences({ lang }) {
  const theme = useTheme();
  const { hitAxios } = React.useContext(GlobalContext);
  const [stats, setStats] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      const res = await hitAxios({
        path: "/api/admin/marketing/audience/stats",
        post: false,
        admin: true,
        showSnackbar: false,
      });
      if (res?.data?.success) setStats(res.data.data);
    })();
  }, [hitAxios]);

  const countFor = (key) => {
    if (!stats) return "…";
    if (key === "marketing_opt_in") return stats.marketing_opt_in;
    if (key === "all_active") return stats.marketing_opt_in;
    return stats[key] ?? "—";
  };

  const openCampaign = (audience) => {
    const url = new URL(window.location.href);
    url.searchParams.set("page", "email-campaigns");
    url.searchParams.set("audience", audience);
    window.history.pushState({}, "", url.toString());
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  return (
    <Box>
      <PageHeader
        title={lang?.marketingAudiences || "Audiences"}
        subtitle="Who you can reach — opt-in gated, with signup recency segments"
        icon={GroupsOutlined}
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Total active users can be higher than campaign reach. Campaigns only email people who
        have a valid address and have not opted out of marketing.
        {stats ? (
          <>
            {" "}
            Right now: <strong>{stats.active_users}</strong> active ·{" "}
            <strong>{stats.marketing_opt_in}</strong> opted in ·{" "}
            <strong>{stats.marketing_opted_out}</strong> opted out.
          </>
        ) : null}
      </Alert>

      <Stack spacing={1.5}>
        {SEGMENTS.map((seg) => (
          <Card
            key={seg.key}
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ sm: "center" }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Box>
                <Typography fontWeight={800}>{seg.label}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {seg.desc}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    px: 1.75,
                    py: 1,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    minWidth: 72,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h6" fontWeight={800} lineHeight={1}>
                    {countFor(seg.key)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    people
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CampaignOutlined />}
                  onClick={() => openCampaign(seg.audience)}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  New campaign
                </Button>
              </Stack>
            </Stack>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}
